import { IShowable } from "../../interfaces/IShowable";
import { RectSize } from "../../utils/RectSize";
import { PlayerSide } from "../ai/TableAI";
import { LoaderFileKeys } from "../constant/AppConstants";
import { OptionsDataManager } from "../managers/OptionsDataManager";
import { AppLocalization, LanguageId } from "../localization/AppLocalization";
import { AppSettings } from "../managers/AppSettings";
import { AppTimings } from "../data/AppConstants";


export enum TableScoreLayout {
    ROUNDED,
    SNAP_BOTTOM,
}

export class TableScore
    extends Phaser.GameObjects.Container
    implements IShowable {
    public grBg: Phaser.GameObjects.Graphics;
    public labelUsTitle: Phaser.GameObjects.Text;
    public labelThemTitle: Phaser.GameObjects.Text;
    public labelUsValue: Phaser.GameObjects.Text;
    public labelThemValue: Phaser.GameObjects.Text;
    public labelTotalScoreUs: Phaser.GameObjects.Text;
    public labelTotalScoreThem: Phaser.GameObjects.Text;
    public verticalLine: Phaser.GameObjects.Image;

    /**
     * Some pixel space for labels, so that everytime new value is set, the box does not always resize (distracts user from game).
     * When these values are exceeded, box will resize.. everytime.
     * 
     * NOTE: Excludes elementSpacing
     * NOTE: labelUs/Them titles dont necessarily need this (as they are set once per language selection), however this allows us to adjust the extra empty space for those labels.
     */
    private labelUsThemMinWidth: number = 110;
    private labelUsThemValueMinWidth: number = 70;
    private labelTotalScoreUsThemMinWidth: number = 70;

    /**
     * Spacing between individual elements
     * - background width and height is scaled to this
     * - considered when aligning elements
     * - NOT space between elements and bg edge, for that use @see bgMargin
     */
    private elementSpacing: Phaser.Geom.Point = new Phaser.Geom.Point(40, 0);
    private grBgMargin: Phaser.Geom.Point = new Phaser.Geom.Point(40, 15);

    private curDealScoreUs: number;
    private curDealScoreThem: number;
    private totalScoreUs: number;
    private totalScoreThem: number;

    private grBgSize: RectSize;
    protected isShowing: boolean = false;
    /**
     * Is currently snapping bottom to trumpView?
     */
    private curLayout: TableScoreLayout;


    public Init(): void {
        this.grBgSize = new RectSize();
        this.curLayout = TableScoreLayout.ROUNDED;
    }

    public Build(): void {
        // bg
        this.grBg = this.scene.make.graphics({ x: 0, y: 0, fillStyle: { color: 0x000000, alpha: 0.8 } });
        this.add(this.grBg);

        const usStyle = {
            color: AppSettings.colors.whiteString,
            fontSize: "35px",
            fontFamily: AppSettings.fontFamilies.MontserratMedium,
            metrics: AppSettings.metrics.montserratMedium35,
        };
        const usValueStyle = {
            color: AppSettings.colors.yellowString,
            fontSize: "50px",
            fontFamily: AppSettings.fontFamilies.MontserratBold,
            metrics: AppSettings.metrics.montserratBold50,
        };
        const totalScoreStyle = {
            color: AppSettings.colors.whiteString,
            fontSize: "30px",
            fontFamily: AppSettings.fontFamilies.MontserratMedium,
            metrics: AppSettings.metrics.montserratMedium30,
        };

        this.labelUsTitle = this.scene.make.text({ style: usStyle });
        this.labelThemTitle = this.scene.make.text({ style: usStyle });
        this.labelUsValue = this.scene.make.text({ text: "0", style: usValueStyle });
        this.labelThemValue = this.scene.make.text({ text: "0", style: usValueStyle });
        this.labelTotalScoreUs = this.scene.make.text({ text: "0", style: totalScoreStyle });
        this.labelTotalScoreThem = this.scene.make.text({ text: "0", style: totalScoreStyle });
        this.verticalLine = this.scene.make.image({ key: LoaderFileKeys.ui, frame: "rect-2x2-white-A50.png" });

        // this.grBg.setOrigin(0,0);
        this.labelUsTitle.setOrigin(1, 0.5);
        this.labelThemTitle.setOrigin(1, 0.5);
        this.labelUsValue.setOrigin(1, 0.5);
        this.labelThemValue.setOrigin(1, 0.5);
        this.labelTotalScoreUs.setOrigin(0, 0.5);
        this.labelTotalScoreThem.setOrigin(0, 0.5);
        this.verticalLine.setOrigin(0.5, 0);

        this.add(this.labelUsTitle);
        this.add(this.labelThemTitle);
        this.add(this.labelUsValue);
        this.add(this.labelThemValue);
        this.add(this.labelTotalScoreUs);
        this.add(this.labelTotalScoreThem);
        this.add(this.verticalLine);
        // console.log(this.labelUsTitle.style["metrics"]);

        // set all scores to defaults (0)
        this.ResetCurDealScores(false);
        this.ResetTotalScores(false);
        // set us/them localized texts
        // > UpdateView
        // NOTE: called by Table.
        // this.UpdateViewFromLocalizationChange(true);
    }

    public Show(animated: boolean = true): void {
        // console.log("TablePopupWindow::Show; animated=", animated);
        // this.scale = 0;
        this.alpha = 0;

        // if animate
        let genericShowDuration = OptionsDataManager.GetTiming(AppTimings.genericShowDuration);
        // genericShowDuration = 5000;
        if (animated) {
            // this.showableTween = 
            this.scene.tweens.add({
                targets: this,
                alpha: 1,
                // scale: 1,
                duration: genericShowDuration,
                ease: AppSettings.ease.genericShowHideEase,
            })
        }
        else {
            //
            this.alpha = 1;
            // this.scale = 1;
        }
        this.isShowing = true;
    }

    public Hide(animated: boolean = true): void {
        // console.log("PopupBase::Hide; animated=", animated);
        // if animate
        const genericHideDuration = OptionsDataManager.GetTiming(AppTimings.genericHideDuration);
        if (animated) {
            // this.showableTween = 
            this.scene.tweens.add({
                targets: this,
                alpha: 0,
                // scale: 0,
                duration: genericHideDuration,
                ease: AppSettings.ease.genericShowHideEase,
            })
        }
        else {
            // 
            this.alpha = 0;
            // this.scale = 0;
        }
        this.isShowing = false;
    }

    public IsShowing(): boolean {
        return this.isShowing;
    }

    public SetCurDealScoreUs(score: number): void {
        this.curDealScoreUs = score;
        this.UpdateView();
    }

    public GetCurDealScoreUs(): number {
        return this.curDealScoreUs;
    }

    public SetCurDealScoreThem(score: number): void {
        this.curDealScoreThem = score;
        this.UpdateView();
    }

    public GetCurDealScoreThem(): number {
        return this.curDealScoreThem;
    }

    /**
     * Reset curDealScoreUs and curDealScoreThem to 0
     */
    public ResetCurDealScores(updateView: boolean = true): void {
        this.curDealScoreUs = 0;
        this.curDealScoreThem = 0;
        if (updateView) {
            this.UpdateView();
        }
    }

    /**
     * Set number of deals won by us
     * @param count number of deals won by us
     */
    public SetTotalScoreUs(count: number): void {
        this.totalScoreUs = count;
        this.UpdateView();
    }

    /**
     * Set number of deals won by us
     * @param count number of deals won by us
     */
    public SetTotalScoreThem(count: number): void {
        this.totalScoreThem = count;
        this.UpdateView();
    }

    public ResetTotalScores(updateView: boolean = true): void {
        this.totalScoreUs = 0;
        this.totalScoreThem = 0;
        if (updateView) {
            this.UpdateView();
        }
    }

    public SetLayout(newLayout: TableScoreLayout, updateView: boolean = false): void {
        if (this.curLayout != newLayout) {
            this.curLayout = newLayout;

            if (updateView) {
                this.UpdateView();
            }
        }
    }

    /**
     * Reset all scores to defaults (0)
     */
    public Reset(): void {
        this.ResetCurDealScores(false);
        this.ResetTotalScores(false);
        this.UpdateView(); // < updates bgSize, TF texts
    }

    public GetSize(): RectSize {
        // console.log(this.grBgSize);
        return this.grBgSize;
    }

    public UpdateViewFromLocalizationChange(languageId: LanguageId, updateView: boolean = true): void {
        const sUs: string = AppLocalization.GetSharedData(languageId).table.scoreView.us;
        const sThem: string = AppLocalization.GetSharedData(languageId).table.scoreView.them;
        this.labelUsTitle.text = sUs;
        this.labelThemTitle.text = sThem;

        if (updateView) {
            this.UpdateView();
        }
    }

    /**
     * reposition individual elements, then rescale background to accomodate them
     */
    public UpdateView(animate: boolean = true): void {
        // console.log("TableScore:UpdateView");

        const curLabelUsThemWidth: number = Math.max(
            this.labelUsTitle.displayWidth,
            this.labelThemTitle.displayWidth,
            this.labelUsThemMinWidth,
        );
        const curLabelUsThemValueWidth: number = Math.max(
            this.labelUsValue.displayWidth,
            this.labelThemValue.displayWidth,
            this.labelUsThemValueMinWidth,
        );
        const curLabelTotalScoreUsThemWidth: number = Math.max(
            this.labelTotalScoreUs.displayWidth,
            this.labelTotalScoreThem.displayWidth,
            this.labelTotalScoreUsThemMinWidth
        );

        this.labelUsValue.text = this.curDealScoreUs.toString();
        this.labelThemValue.text = this.curDealScoreThem.toString();
        this.labelTotalScoreUs.text = this.totalScoreUs.toString();
        this.labelTotalScoreThem.text = this.totalScoreThem.toString();

        // component width excluding spacing/margins/padding
        const widthNoGaps: number = curLabelUsThemWidth + curLabelUsThemValueWidth + curLabelTotalScoreUsThemWidth;

        /*
            labelUsTitle(Us) 46 
            labelThemTitle(Them) 103 
            labelUsValue(131) 69 
            labelThemValue(31) 50 
            labelTotalScoreUs(0) 21 
            labelTotalScoreThem(0) 21

            labelUsTitle(Us) 46 
            labelThemTitle(Them) 103 
            labelUsValue(57) 61 
            labelThemValue(105) 84 
            labelTotalScoreUs(865) 56 
            labelTotalScoreThem(1139) 58
        */
        // console.log(
        //     "labelUsTitle(" + this.labelUsTitle.text + ")", this.labelUsTitle.displayWidth,
        //     "labelThemTitle(" + this.labelThemTitle.text + ")", this.labelThemTitle.displayWidth,
        //     "labelUsValue(" + this.labelUsValue.text + ")", this.labelUsValue.displayWidth,
        //     "labelThemValue(" + this.labelThemValue.text + ")", this.labelThemValue.displayWidth,
        //     "labelTotalScoreUs(" + this.labelTotalScoreUs.text + ")", this.labelTotalScoreUs.displayWidth,
        //     "labelTotalScoreThem(" + this.labelTotalScoreThem.text + ")", this.labelTotalScoreThem.displayWidth,
        // );

        // height of tallest element
        const maxElementHeight: number = Math.max(
            this.labelUsTitle.displayHeight,
            this.labelUsValue.displayHeight,
            this.labelTotalScoreUs.displayHeight,
        );

        // hitArea
        this.grBgSize.Set(
            widthNoGaps + 2 * this.grBgMargin.x + 3 * this.elementSpacing.x,
            2 * maxElementHeight + 2 * this.grBgMargin.y + this.elementSpacing.y,
        );

        let extraHeightFromLayout: number;
        let roundedRectRadius: Phaser.Types.GameObjects.Graphics.RoundedRectRadius;
        switch (this.curLayout) {
            case TableScoreLayout.ROUNDED:
                roundedRectRadius = { tl: 20, tr: 20, br: 20, bl: 20 };
                extraHeightFromLayout = 0;
                break;
            case TableScoreLayout.SNAP_BOTTOM:
                roundedRectRadius = { tl: 20, tr: 20, br: 0, bl: 0 };
                extraHeightFromLayout = 0;
                break;
        }

        this.grBg.clear();
        this.grBg.fillStyle(this.grBg.defaultFillColor, this.grBg.defaultFillAlpha);
        this.grBg.fillRoundedRect(0, 0, this.grBgSize.width, this.grBgSize.height + extraHeightFromLayout, roundedRectRadius);

        const labelUsPosX: number = this.grBgMargin.x + curLabelUsThemWidth;
        const row1PosY: number = this.grBgMargin.y + maxElementHeight * 0.5;
        const row2PosY: number = this.grBgMargin.y + this.elementSpacing.y + maxElementHeight * 1.5;

        this.labelUsTitle.setPosition(labelUsPosX, row1PosY);
        this.labelThemTitle.setPosition(labelUsPosX, row2PosY);

        const labelUsValuePosX: number = labelUsPosX + this.elementSpacing.x + curLabelUsThemValueWidth;

        this.labelUsValue.setPosition(labelUsValuePosX, row1PosY);
        this.labelThemValue.setPosition(labelUsValuePosX, row2PosY);

        const verticalLineX: number = labelUsValuePosX + this.elementSpacing.x;
        this.verticalLine.setPosition(verticalLineX, 25);
        this.verticalLine.displayHeight = this.grBgSize.height - 50;

        const labelroundsWonUsPosX: number = verticalLineX + this.elementSpacing.x;
        this.labelTotalScoreUs.setPosition(labelroundsWonUsPosX, row1PosY);
        this.labelTotalScoreThem.setPosition(labelroundsWonUsPosX, row2PosY);
    }
}